import { Icon, IconName, IconSize } from "@/components/icons/Icon";
import { FieldLabelV2 } from "@/components/interactive/FieldLabelV2";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import { Skeleton } from "@/components/loaders/Skeleton";
import { useKeyControls } from "@/components/resolver/useKeyControls";
import { DesignSystemTypography, TextVariant } from "@/components/typography/design-system";
import { classNames } from "@/utils/classnames";
import React, { ForwardedRef, forwardRef, ReactNode, useId, useMemo, useState } from "react";

// ------------------------------------------------------------------
// InputV2 — dashboard text input with a required label, optional
// description, optional left icon, and an optional error message.
//
// Sizes match ButtonV2: `sm` = 28px, `md` = 32px.
//
// Visual states (placeholder / default / focused / error / disabled)
// are derived from runtime state — not an explicit `state` prop.
//   - placeholder / default  → whether `value` is empty (native browser)
//   - focused                → CSS `:focus-within` on the container
//   - error                  → the `error` prop (string) is present
//   - disabled               → the native `disabled` attribute
// ------------------------------------------------------------------

export const InputV2Sizes = ["sm", "md"] as const;
export type InputV2Size = (typeof InputV2Sizes)[number];

// Container (border + padding + height) per size.
const containerSizeClasses: Record<InputV2Size, string> = {
  sm: "h-7 pl-cds-8 gap-cds-4",
  md: "h-8 pl-cds-8 gap-cds-6",
};

const compoundContainerSizeClasses: Record<InputV2Size, string> = {
  sm: "h-7",
  md: "h-8",
};

// Gap between the left icon and the typed value. Lives on the inner row — the
// outer container's gap only separates integrated segments (prefix select /
// adornment / trailing action), so it never reaches the icon.
const inputRowGapClasses: Record<InputV2Size, string> = {
  sm: "gap-cds-4",
  md: "gap-cds-6",
};

const containerRightPaddingClasses: Record<InputV2Size, string> = {
  sm: "pr-cds-8",
  md: "pr-cds-8",
};

const integratedSelectSizeClasses: Record<InputV2Size, string> = {
  sm: "h-7 pl-cds-8 pr-cds-6 gap-cds-6",
  md: "h-8 pl-cds-8 pr-cds-6 gap-cds-8",
};

const integratedAdornmentSizeClasses: Record<InputV2Size, string> = {
  sm: "h-7 px-cds-8",
  md: "h-8 px-cds-8",
};

// Icon size inside the input — same mapping as ButtonV2.
const inputIconSize: Record<InputV2Size, IconSize> = {
  sm: "sm", // 12px
  md: "md", // 16px
};

// Text size for the input value and placeholder.
// Dashboard DS typography scale — resolves to CSS vars defined on
// the `.design-system-typography` ancestor applied by Layout.
// `sm` = 12px, `md` = 13px.
const inputTextVariant: Record<InputV2Size, TextVariant> = {
  sm: "sm",
  md: "md",
};

// Error-message text size per input size. Label + description sizes
// are owned by FieldLabelV2.
// Small inputs use 11px (`xs`); medium stays at 12px (`sm`).
const errorTextVariant: Record<InputV2Size, TextVariant> = {
  sm: "xs",
  md: "sm",
};

const CHEVRON_SIZE_CLASS = "!w-3.5 !h-3.5 !min-w-3.5 !min-h-3.5";

export interface InputV2IntegratedSelectOption {
  value: string;
  label: ReactNode;
}

export interface InputV2IntegratedSelectControl {
  ariaLabel: string;
  options: readonly InputV2IntegratedSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  widthClassName?: string;
}

export interface InputV2Props extends Omit<React.ComponentProps<"input">, "size" | "ref"> {
  size?: InputV2Size;
  /** Keep the field's real label and geometry while its value is loading. */
  loading?: boolean;
  /** Prefer visible labels; composite controls may provide an external label via `htmlFor`. */
  label?: string;
  /** Visually hide the label (still announced by screen readers via `sr-only`).
   *  For inline/compact inputs — e.g. an inline rename — where a visible label
   *  doesn't fit. `label` stays required so the control keeps an accessible name. */
  hideLabel?: boolean;
  /** Appends a red `*` to the label and sets `aria-required`. */
  required?: boolean;
  /** Helper text rendered under the label. */
  description?: string;
  /** Compact action rendered inline after the visible label. */
  labelTrailingAction?: ReactNode;
  /** Named icon rendered on the left inside the input. */
  leftIcon?: IconName;
  /** Compact interactive/non-interactive element rendered on the right inside the input chrome. */
  trailingAction?: ReactNode;
  /** Yellow edited-state tint for forms that need to call out changed values. */
  dirty?: boolean;
  /** When present: red border + inline error message below the input. */
  error?: string;
  /** Integrated hardcoded option selector attached to the leading edge of the input chrome. */
  prefixSelect?: InputV2IntegratedSelectControl;
  /** Static segment attached after the leading selector and before the typed value. */
  prefixAdornment?: ReactNode;
  /** Integrated hardcoded option selector attached to the trailing edge of the input chrome. */
  suffixSelect?: InputV2IntegratedSelectControl;
  /** Escape hatch to add classes to the input container (the bordered box
   *  around the input itself). Primarily useful in showcases/stories that
   *  need to force visual states (e.g. applying `!shadow-cds-focus` to
   *  preview the focus look statically). */
  containerClassName?: string;
}

export const InputV2 = forwardRef(function InputV2Impl(
  {
    size = "md",
    loading = false,
    label,
    hideLabel = false,
    required = false,
    description,
    labelTrailingAction,
    leftIcon,
    trailingAction,
    dirty = false,
    error,
    prefixSelect,
    prefixAdornment,
    suffixSelect,
    id: idFromProps,
    disabled: disabledProp,
    className,
    containerClassName,
    ...rest
  }: InputV2Props,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const disabled = disabledProp || loading;
  // Stable ids for label/description/error associations. `useId` gives a
  // deterministic SSR-safe id when the caller doesn't provide one.
  const autoId = useId();
  const id = idFromProps ?? `input-${autoId}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const ariaDescribedBy = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;
  const hasVisibleLabel = label != null && label !== "";
  const hasPrefixSelect = prefixSelect != null;
  const hasPrefixAdornment = prefixAdornment != null;
  const hasSuffixSelect = suffixSelect != null;
  const hasIntegratedControl = hasPrefixSelect || hasPrefixAdornment || hasSuffixSelect;
  const showDirtyState = dirty && !disabled && !error;

  return (
    // `data-ds-v2` opts this subtree out of `dark-mode.css`'s
    // `.dark input` / `::placeholder` / `label` global overrides.
    // Without it, a V2 input sitting under `html.dark` (including
    // the `.theme-light` preview subtree) gets its typed-value and
    // placeholder colors flipped by the class+element specificity
    // of the legacy selectors.
    <div data-ds-v2={""} className={classNames("w-full", className)}>
      {hasVisibleLabel && (
        <FieldLabelV2
          label={label}
          required={required}
          description={description}
          size={size}
          htmlFor={id}
          descriptionId={descriptionId}
          trailingAction={labelTrailingAction}
          hidden={hideLabel}
        />
      )}

      {/* Input container (border + bg + focus ring). Integrated controls
          share this frame so focus-within draws one ring around the whole
          conceptual field, not separate rings around each segment. */}
      <div
        className={classNames(
          "relative flex w-full items-center rounded-cds-6 border-[1px]",
          loading && "[&>*:not(.loading-skeleton)]:invisible",
          "bg-cds-surface-default dark:bg-white/[0.04]",
          "transition-colors duration-75",
          !disabled && "shadow-cds-s focus-within:shadow-cds-focus",
          error ? "border-cds-error" : "border-cds-surface-border",
          "dark:!border-white/[0.08]",
          showDirtyState &&
            "border-yellow-300 bg-yellow-50/60 dark:!border-yellow-400/30 dark:bg-yellow-500/10",
          disabled && "cursor-not-allowed",
          disabled && "bg-cds-surface-active dark:bg-white/[0.04]",
          hasIntegratedControl ? compoundContainerSizeClasses[size] : containerSizeClasses[size],
          !hasIntegratedControl && (trailingAction ? "pr-0" : containerRightPaddingClasses[size]),
          containerClassName,
        )}
      >
        {loading && (
          <Skeleton
            aria-label={label ? `Loading ${label}` : "Loading field"}
            ariaHidden={false}
            className="loading-skeleton absolute inset-0 h-full w-full"
            radius="sm"
            role="status"
          />
        )}
        {prefixSelect && (
          <InputV2IntegratedSelect
            control={prefixSelect}
            disabled={disabled}
            dirty={showDirtyState}
            position={"prefix"}
            size={size}
          />
        )}
        {hasPrefixAdornment && (
          <InputV2IntegratedAdornment dirty={showDirtyState} disabled={disabled} size={size}>
            {prefixAdornment}
          </InputV2IntegratedAdornment>
        )}
        <div
          className={classNames(
            "flex h-full min-w-0 flex-1 items-center",
            inputRowGapClasses[size],
            hasIntegratedControl && "pl-cds-8",
            hasIntegratedControl && !trailingAction && containerRightPaddingClasses[size],
          )}
        >
          {leftIcon && (
            <Icon
              name={leftIcon}
              size={inputIconSize[size]}
              className={classNames("!text-cds-ink-muted shrink-0", disabled && "dark:opacity-50")}
            />
          )}
          <input
            ref={ref}
            id={id}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-required={required || undefined}
            aria-describedby={ariaDescribedBy}
            className={classNames(
              // Aggressive reset of @tailwindcss/forms base styling. The
              // plugin (loaded with the default "base" strategy) applies
              // its own 1px border, 0.5rem/0.75rem padding, shadow, and a
              // blue focus ring directly to every raw <input> — all of
              // which fight the container's styling and produce a visible
              // inner outline. `!` guarantees these beat the base layer
              // selectors (e.g. `[type='text']`) regardless of source order.
              "!p-0 !border-0 !bg-transparent !shadow-none",
              "focus:!ring-0 focus:!ring-offset-0 focus:!border-0",
              "focus:!outline-none focus:!shadow-none",
              "[&::-webkit-search-cancel-button]:appearance-none",
              "[&::-webkit-search-cancel-button]:hidden",
              // DS styling — container handles chrome, <input> handles text.
              // Colors declared as arbitrary hex so nothing (legacy
              // `.dark input`, @tailwindcss/forms base placeholder,
              // body-color inheritance) can out-specificity us.
              //   typed value: zinc-800 light / zinc-200 dark
              //   placeholder: zinc-400 light / zinc-500 dark
              "flex-1 min-w-0",
              "!text-[#27272a] placeholder:!text-[#a1a1aa]",
              "dark:!text-[#e4e4e7] dark:placeholder:!text-[#71717a]",
              "disabled:cursor-not-allowed",
              // Disabled: mirror SelectV2's disabled text treatment —
              // zinc-400 light (#a1a1aa, matches placeholder tone) /
              // zinc-500 dark (#71717a) + 50% opacity in dark.
              "disabled:!text-[#a1a1aa] dark:disabled:!text-[#71717a]",
              "dark:disabled:opacity-50",
              DesignSystemTypography[inputTextVariant[size]],
            )}
            {...rest}
          />
          {trailingAction && (
            <div className={"-mr-px flex shrink-0 items-center"}>{trailingAction}</div>
          )}
        </div>
        {suffixSelect && (
          <InputV2IntegratedSelect
            control={suffixSelect}
            disabled={disabled}
            dirty={showDirtyState}
            position={"suffix"}
            size={size}
          />
        )}
      </div>

      {/* Error message */}
      {error && (
        <p
          id={errorId}
          role={"alert"}
          // `!` on the color beats body-color inheritance under
          // `html.dark body { color: zinc-200 }` which would
          // otherwise render the alert in light grey instead of red.
          className={classNames(
            "pt-cds-6 pl-cds-8 !text-cds-error",
            DesignSystemTypography[errorTextVariant[size]],
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
});

export interface SegmentedInputV2Segment {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Width of the segment's field in ch (default 2 — two digits). */
  widthCh?: number;
  inputMode?: "numeric" | "text";
}

export interface SegmentedInputV2Props {
  label: string;
  hideLabel?: boolean;
  size?: InputV2Size;
  description?: string;
  /** The typed fields, in order; `separator` renders between adjacent ones. */
  segments: readonly SegmentedInputV2Segment[];
  /** Static glyph between segments (e.g. ":" for a time field). */
  separator?: ReactNode;
  /** Integrated hardcoded option selector on the trailing edge (e.g. AM/PM). */
  suffixSelect?: InputV2IntegratedSelectControl;
  error?: string;
  dirty?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * SegmentedInputV2 — several short typed fields sharing one InputV2 frame,
 * with static separators between them and an optional integrated suffix
 * selector. For composite values that read as one field (a time as
 * `hh : mm` + AM/PM). Focus-within draws the frame's single ring, exactly
 * like InputV2's prefix/suffix-select composites.
 */
export const SegmentedInputV2: React.FC<SegmentedInputV2Props> = ({
  label,
  hideLabel = false,
  size = "md",
  description,
  segments,
  separator,
  suffixSelect,
  error,
  dirty = false,
  disabled,
  className,
}) => {
  const autoId = useId();
  const firstSegmentId = `segmented-input-${autoId}`;
  const descriptionId = description ? `${firstSegmentId}-description` : undefined;
  const errorId = error ? `${firstSegmentId}-error` : undefined;
  // Mid-edit segment values are transiently invalid (a cleared hour about to
  // be retyped), so the error presentation waits for focus to leave the
  // composite; validation reads as a blur/submit concern.
  const [focusWithin, setFocusWithin] = useState(false);
  const showError = error != null && !focusWithin;
  const showDirtyState = dirty && !disabled && !showError;

  return (
    <div
      data-ds-v2={""}
      className={classNames("w-fit", className)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocusWithin(false);
        }
      }}
    >
      <FieldLabelV2
        label={label}
        size={size}
        htmlFor={firstSegmentId}
        description={description}
        descriptionId={descriptionId}
        hidden={hideLabel}
      />
      <div
        className={classNames(
          "flex items-center rounded-cds-6 border-[1px]",
          "bg-cds-surface-default dark:bg-white/[0.04]",
          "transition-colors duration-75",
          !disabled && "shadow-cds-s focus-within:shadow-cds-focus",
          showError ? "border-cds-error" : "border-cds-surface-border",
          "dark:!border-white/[0.08]",
          showDirtyState &&
            "border-yellow-300 bg-yellow-50/60 dark:!border-yellow-400/30 dark:bg-yellow-500/10",
          disabled && "cursor-not-allowed bg-cds-surface-active dark:bg-white/[0.04]",
          compoundContainerSizeClasses[size],
        )}
      >
        <div className={classNames("flex items-center px-cds-8", inputRowGapClasses[size])}>
          {segments.map((segment, index) => (
            <React.Fragment key={index}>
              {index > 0 && separator != null && (
                <span
                  aria-hidden
                  className={classNames(
                    "select-none text-cds-ink-subtle dark:text-zinc-500",
                    DesignSystemTypography[inputTextVariant[size]],
                  )}
                >
                  {separator}
                </span>
              )}
              <input
                id={index === 0 ? firstSegmentId : undefined}
                aria-label={segment.ariaLabel}
                aria-invalid={showError ? true : undefined}
                aria-describedby={[descriptionId, errorId].filter(Boolean).join(" ") || undefined}
                inputMode={segment.inputMode ?? "numeric"}
                placeholder={segment.placeholder}
                disabled={disabled}
                value={segment.value}
                onChange={(event) => segment.onChange(event.target.value)}
                style={{ width: `${segment.widthCh ?? 2}ch` }}
                className={classNames(
                  // Same @tailwindcss/forms reset as InputV2's field.
                  "!p-0 !border-0 !bg-transparent !shadow-none box-content",
                  "focus:!ring-0 focus:!ring-offset-0 focus:!border-0",
                  "focus:!outline-none focus:!shadow-none",
                  "text-center",
                  "!text-[#27272a] placeholder:!text-[#a1a1aa]",
                  "dark:!text-[#e4e4e7] dark:placeholder:!text-[#71717a]",
                  "disabled:cursor-not-allowed",
                  "disabled:!text-[#a1a1aa] dark:disabled:!text-[#71717a]",
                  "dark:disabled:opacity-50",
                  DesignSystemTypography[inputTextVariant[size]],
                )}
              />
            </React.Fragment>
          ))}
        </div>
        {suffixSelect && (
          <InputV2IntegratedSelect
            control={suffixSelect}
            disabled={disabled}
            dirty={showDirtyState}
            position={"suffix"}
            size={size}
          />
        )}
      </div>
      {showError && (
        <p
          id={errorId}
          role={"alert"}
          className={classNames(
            "pt-cds-6 pl-cds-8 !text-cds-error",
            DesignSystemTypography[errorTextVariant[size]],
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
};

const InputV2IntegratedAdornment = ({
  children,
  dirty,
  disabled,
  size,
}: {
  children: ReactNode;
  dirty?: boolean;
  disabled?: boolean;
  size: InputV2Size;
}) => (
  <div
    className={classNames(
      "flex shrink-0 items-center border-r bg-cds-surface-active/70 dark:bg-white/[0.03]",
      dirty
        ? "!border-yellow-300 dark:!border-yellow-400/30"
        : "border-cds-surface-border dark:border-white/[0.08]",
      disabled
        ? "!text-[#a1a1aa] dark:!text-[#71717a] dark:opacity-50"
        : "text-cds-ink-subtle dark:text-zinc-400",
      integratedAdornmentSizeClasses[size],
      DesignSystemTypography[inputTextVariant[size]],
    )}
  >
    {children}
  </div>
);

const InputV2IntegratedSelect = ({
  control,
  dirty,
  disabled,
  position,
  size,
}: {
  control: InputV2IntegratedSelectControl;
  dirty?: boolean;
  disabled?: boolean;
  position: "prefix" | "suffix";
  size: InputV2Size;
}) => {
  const { open, setOpen, wrapperRef } = useKeyControls();
  const isDisabled = disabled || control.disabled;
  const selectedOption = useMemo(
    () => control.options.find((option) => option.value === control.value),
    [control.options, control.value],
  );
  const sizingLabel = control.options.reduce<ReactNode>(
    (widestLabel, option) =>
      String(option.label).length > String(widestLabel).length ? option.label : widestLabel,
    control.options[0]?.label ?? control.value,
  );

  return (
    <div
      ref={wrapperRef}
      className={classNames("relative shrink-0", control.widthClassName ?? "w-fit")}
    >
      <button
        type={"button"}
        aria-label={control.ariaLabel}
        aria-haspopup={"menu"}
        aria-expanded={open}
        disabled={isDisabled}
        className={classNames(
          "flex w-full items-center self-stretch bg-transparent",
          "focus:outline-none transition-colors duration-75",
          isDisabled && "cursor-not-allowed",
          position === "prefix" &&
            (dirty
              ? "border-r !border-yellow-300 dark:!border-yellow-400/30"
              : "border-r border-cds-surface-border dark:border-white/[0.08]"),
          position === "suffix" &&
            (dirty
              ? "border-l !border-yellow-300 dark:!border-yellow-400/30"
              : "border-l border-cds-surface-border dark:border-white/[0.08]"),
          integratedSelectSizeClasses[size],
        )}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span
          className={classNames(
            "grid min-w-0 flex-1 text-left",
            isDisabled
              ? "!text-[#a1a1aa] dark:!text-[#71717a] dark:opacity-50"
              : "!text-[#27272a] dark:!text-[#e4e4e7]",
            DesignSystemTypography[inputTextVariant[size]],
          )}
        >
          <span className={"col-start-1 row-start-1 truncate"}>
            {selectedOption?.label ?? control.value}
          </span>
          <span className={"invisible col-start-1 row-start-1 whitespace-nowrap"} aria-hidden>
            {sizingLabel}
          </span>
        </span>
        <Icon
          name={"chevronsUpDown"}
          className={classNames(
            CHEVRON_SIZE_CLASS,
            "shrink-0",
            isDisabled ? "!text-[#d4d4d8] dark:!text-[#71717a]" : "!text-cds-ink-subtle",
            isDisabled && "dark:opacity-50",
          )}
        />
      </button>
      {open && (
        <div className={"absolute left-0 right-0 z-[70] mt-cds-4"}>
          <MenuContainerV2>
            <MenuSectionV2>
              {control.options.map((option) => (
                <MenuItemV2
                  key={option.value}
                  variant={"default"}
                  size={"sm"}
                  label={option.label}
                  selected={option.value === control.value}
                  onClick={() => {
                    control.onChange(option.value);
                    setOpen(false);
                  }}
                />
              ))}
            </MenuSectionV2>
          </MenuContainerV2>
        </div>
      )}
    </div>
  );
};
